/**
 * 🎰 ConsumablesShop — Auction House shop for non-gear consumables.
 * Currently sells: Extra Spin Tokens (2 OD · 300 Chits · 100 Phoenix).
 *
 * Endpoints:
 *   GET  /api/auction-house/consumables
 *   POST /api/auction-house/consumables/buy  { item_id, currency, qty }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCY_ICON = { od: "💎", chits: "🪙", phoenix: "🐦‍🔥", spirit: "🍚", battle_coins: "⚔️" };
const CURRENCY_LABEL = { od: "OD", chits: "Chits", phoenix: "Phoenix Tokens", spirit: "Spirit", battle_coins: "BC" };
const WALLET_FIELD = {
  od:            "omni_dollars",
  chits:         "chits",
  phoenix:       "phoenix_tokens",
  spirit:        "brawler_spirit",
  battle_coins:  "battle_coins",
};

export default function ConsumablesShop() {
  const [items, setItems] = useState([]);
  const [wallet, setWallet] = useState({});
  const [busy, setBusy] = useState(false);
  const [qty, setQty] = useState({});     // per-item qty state

  const refresh = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(`${API}/auction-house/consumables`),
        axios.get(`${API}/auction-house/state`),
      ]);
      setItems(c.data.consumables || []);
      setWallet(s.data.wallet || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Consumables load failed.");
    }
  };

  useEffect(() => { refresh(); }, []);

  const buy = async (item_id, currency) => {
    const n = Math.max(1, parseInt(qty[item_id] || 1));
    const item = items.find((x) => x.id === item_id);
    const row = item?.prices?.[currency];
    const total = (row?.amount || 0) * n;
    // 🧾 Roc #38 — confirm before spending.
    const ok = await confirmDialog({
      title: `Buy ${n}× ${item?.name || item_id}?`,
      message: `Cost: ${CURRENCY_ICON[currency]} ${total.toLocaleString()} ${CURRENCY_LABEL[currency]}.
Will deduct from your balance immediately.`,
      confirmLabel: `Buy — ${total.toLocaleString()} ${CURRENCY_LABEL[currency]}`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/auction-house/consumables/buy`, {
        item_id, currency, qty: n,
      });
      toast.success(`${r.data.icon || "✅"} +${r.data.qty} ${r.data.name} (total ${r.data.booster_total})`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    }
    setBusy(false);
  };

  return (
    <div data-testid="consumables-shop" className="space-y-4">
      <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-[#1a0522] via-black/40 to-[#22001a] p-4">
        <div className="text-2xl font-black tracking-wide text-purple-200" style={{ fontFamily: "Anton, sans-serif" }}>
          🎰 CONSUMABLES SHOP
        </div>
        <div className="text-[11px] text-neutral-400 mt-1 max-w-2xl">
          Instant-buy tokens & boosters. Every purchase drops straight into your inventory — no bidding, no timers.
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="consumables-grid">
        {items.map((it) => (
          <ItemCard
            key={it.id}
            item={it}
            wallet={wallet}
            qty={qty[it.id] || 1}
            onQtyChange={(v) => setQty((q) => ({ ...q, [it.id]: v }))}
            onBuy={(cur) => buy(it.id, cur)}
            busy={busy}
          />
        ))}
        {items.length === 0 && (
          <div className="text-center text-neutral-500 text-xs p-6 col-span-full">
            No consumables in stock right now — check back soon.
          </div>
        )}
      </div>
    </div>
  );
}

function ItemCard({ item, wallet, qty, onQtyChange, onBuy, busy }) {
  const isLocked = !!item.locked;
  return (
    <div
      className={
        "rounded-2xl border p-4 flex flex-col gap-3 relative " +
        (isLocked
          ? "border-amber-500/40 bg-gradient-to-br from-amber-950/40 via-black/60 to-rose-950/40"
          : "border-purple-500/30 bg-black/50")
      }
      data-testid={`consumable-card-${item.id}`}
    >
      {isLocked && (
        <div
          className="absolute top-3 right-3 flex items-center gap-1 rounded-full border border-amber-400/60 bg-black/70 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.3)]"
          data-testid={`consumable-locked-badge-${item.id}`}
        >
          🔒 Locked
        </div>
      )}
      <div className="flex items-start gap-3">
        <div className="text-4xl">{item.icon}</div>
        <div className="flex-1 min-w-0">
          <div className={`text-lg font-black leading-tight ${isLocked ? "text-amber-200" : "text-purple-200"}`}>
            {item.name}
          </div>
          {item.rarity && (
            <div className="text-[9px] uppercase tracking-widest text-amber-400/80 mt-0.5">
              {item.rarity}
            </div>
          )}
          <div className="text-[11px] text-neutral-400 mt-0.5">{item.description}</div>
        </div>
      </div>

      {isLocked ? (
        <>
          {item.lock_reason && (
            <div className="text-[11px] italic text-amber-200/80 border-l-2 border-amber-500/50 pl-2 py-1 bg-amber-950/20 rounded-r">
              {item.lock_reason}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 opacity-60 pointer-events-none">
            {Object.entries(item.prices).map(([cur, row]) => (
              <div
                key={cur}
                className="px-3 py-2 rounded-lg text-sm font-bold border border-amber-500/25 bg-amber-950/20 text-amber-300/70 text-center"
                data-testid={`consumable-buy-${item.id}-${cur}-locked`}
              >
                <span className="text-base leading-none">{CURRENCY_ICON[cur]}</span>{" "}
                <span className="ml-1">{row.amount.toLocaleString()}</span>{" "}
                <span className="text-[10px] opacity-80">{CURRENCY_LABEL[cur]}</span>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-center text-amber-400/80 font-bold uppercase tracking-widest">
            Purchase unlocks with future update
          </div>
        </>
      ) : (
        <>
          {/* Qty picker */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Qty</span>
            <button
              onClick={() => onQtyChange(Math.max(1, qty - 1))}
              disabled={busy}
              className="w-7 h-7 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-purple-200 font-bold"
              data-testid={`consumable-qty-dec-${item.id}`}
            >−</button>
            <input
              type="number"
              min="1"
              max={item.max_stack || 99}
              value={qty}
              onChange={(e) => onQtyChange(Math.max(1, Math.min(item.max_stack || 99, parseInt(e.target.value) || 1)))}
              className="w-14 px-2 py-1 rounded bg-black/70 border border-neutral-600 text-center text-purple-100 text-sm"
              data-testid={`consumable-qty-input-${item.id}`}
            />
            <button
              onClick={() => onQtyChange(Math.min(item.max_stack || 99, qty + 1))}
              disabled={busy}
              className="w-7 h-7 rounded bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-purple-200 font-bold"
              data-testid={`consumable-qty-inc-${item.id}`}
            >+</button>
          </div>

          {/* Buy buttons — one per currency */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {Object.entries(item.prices).map(([cur, row]) => {
              const total = row.amount * qty;
              const balance = wallet[WALLET_FIELD[cur]] ?? 0;
              const affordable = balance >= total;
              return (
                <button
                  key={cur}
                  onClick={() => onBuy(cur)}
                  disabled={busy || !affordable}
                  data-testid={`consumable-buy-${item.id}-${cur}`}
                  className={
                    "px-3 py-2 rounded-lg text-sm font-bold border transition-all " +
                    (affordable
                      ? "bg-purple-600/80 hover:bg-purple-500 border-purple-400/50 text-white shadow-[0_0_18px_rgba(168,85,247,0.25)]"
                      : "bg-neutral-800/60 border-neutral-700 text-neutral-500 cursor-not-allowed")
                  }
                  title={`You have ${balance.toLocaleString()} ${CURRENCY_LABEL[cur]}`}
                >
                  <span className="text-base leading-none">{CURRENCY_ICON[cur]}</span>{" "}
                  <span className="ml-1">{total.toLocaleString()}</span>{" "}
                  <span className="text-[10px] opacity-80">{CURRENCY_LABEL[cur]}</span>
                </button>
              );
            })}
          </div>

          {/* Wallet summary */}
          <div className="text-[10px] text-neutral-500 flex flex-wrap gap-2 items-center">
            <span>Your wallet:</span>
            {Object.keys(item.prices).map((cur) => (
              <span key={cur} className="px-1.5 py-0.5 rounded bg-neutral-900 border border-neutral-700 text-neutral-300">
                {CURRENCY_ICON[cur]} {(wallet[WALLET_FIELD[cur]] || 0).toLocaleString()}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
